import { startCase } from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
  getNocPassport,
  getAllNocPassport,
  INocPassport,
} from "../../../core/data/redux/nocPassportSlice";
import type { RootState } from "../../../core/data/redux/store";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { all_routes } from "../../router/all_routes";
import NOCChatComponent from "../../../components/NOCChat";
import { toast } from "react-toastify";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";

import {
  SapRequestStatus,
  getRequestStatusBadgeClass as getGlobalStatusBadgeClass,
  getRequestStatusLabel as getGlobalStatusLabel,
} from "../../../core/data/constants/requestStatus";

interface NOCPassportDetailsProps { }

const NOCPassportDetails = (props: NOCPassportDetailsProps) => {
  const routes = all_routes;
  const { id } = useParams();
  const dispatch = useDispatch<any>();

  // Get data from Redux state
  const { currentPassport, loading, data: listData } = useSelector(
    (state: RootState) => state.nocPassportState
  );
  const employeesState = useSelector((state: any) => state.employeesState);

  // Local state for the actual application data
  const [applicationData, setApplicationData] = useState<INocPassport | null>(
    null
  );

  // Load data from Redux state
  useEffect(() => {
    if (id) {
      dispatch(getNocPassport({ id: id }));
      if (!listData || listData.length === 0) {
        dispatch(getAllNocPassport({ limit: 100 }));
      }
    }
  }, [id, dispatch]); // Not including listData as dependency to avoid infinite loops if it remains empty

  useEffect(() => {
    if (currentPassport) {
      const listItem = listData?.find((item: any) => String(item.id) === String(id));

      const getValidArray = (arr1: any, arr2: any) => {
        if (Array.isArray(arr1) && arr1.length > 0) return arr1;
        if (Array.isArray(arr2) && arr2.length > 0) return arr2;
        return undefined;
      };

      const mergedData = {
        ...currentPassport,
        remarks_data: getValidArray((currentPassport as any).remarks_data, (listItem as any)?.remarks_data),
        work_history: getValidArray((currentPassport as any).work_history, (listItem as any)?.work_history),
        history: getValidArray((currentPassport as any).history, (listItem as any)?.history),
        remarksHistory: getValidArray((currentPassport as any).remarksHistory, (listItem as any)?.remarksHistory),
      } as any;

      console.log("Details vs List Debug:", { currentPassport, listItem, mergedData });

      setApplicationData(mergedData);
    }
  }, [currentPassport, listData, id]);

  // Helper functions
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return "-";
    }
  };

  // Helper function to convert y/n to yes/no
  const formatYesNo = (value: string | undefined) => {
    if (!value) return "-";
    const lowerValue = value.toLowerCase();
    if (lowerValue === "y" || lowerValue === "yes") return "Yes";
    if (lowerValue === "n" || lowerValue === "no") return "No";
    return value;
  };

  // Passport Type Mapping
  const passportTypeMap: { [key: string]: string } = {
    'O': 'Official',
    'R': 'Regular',
    'D': 'Diplomatic'
  };

  // Helper function to get passport type label
  const getPassportTypeLabel = (value: string | undefined): string => {
    if (!value) return "-";
    return passportTypeMap[value] || value;
  };

  const getStatusBadgeClass = (status: string | null | undefined, isDraft?: boolean) => {
    if (isDraft) return "badge bg-secondary-subtle text-secondary border border-secondary-subtle";
    if (!status) return "badge bg-secondary-subtle text-secondary border border-secondary-subtle";
    const code = status.toString().trim().toUpperCase();
    if (code === "D") return "badge bg-success-subtle text-success border border-success-subtle";
    return getGlobalStatusBadgeClass(code);
  };

  const getStatusText = (status: string | null | undefined, isDraft?: boolean) => {
    if (isDraft) return "Draft";
    if (!status) return "-";
    const code = status.toString().trim().toUpperCase();
    if (code === SapRequestStatus.APPROVED) return "Approved";
    if (code === "D") return "Approved";
    if (code === SapRequestStatus.REJECTED) return "Rejected";
    return getGlobalStatusLabel(code);
  };

  // Get NOC Applied For text
  const getNOCAppliedForText = (value: string | undefined) => {
    if (!value) return "-";
    switch (value) {
      case "0":
        return "Fresh Passport";
      case "1":
        return "Renewal of Passport";
      case "2":
        return "Visa";
      default:
        return value;
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "50vh" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!applicationData) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-warning">
            <i className="ti ti-alert-circle me-2"></i>
            NOC request not found. Please go back to the main page and try
            again.
          </div>
          <Link to={routes.noc.passport} className="btn btn-primary">
            <i className="ti ti-arrow-left me-2"></i>
            Back to NOC List
          </Link>
        </div>
      </div>
    );
  }

  const profileUser = employeesState?.data?.data || employeesState?.data;
  const profileDetails = profileUser?.details || profileUser || {};
  const storedUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : {};
    } catch {
      return {};
    }
  })();
  const storedDetails = storedUser?.details || storedUser || {};

  const empDetails = (applicationData as any).employee || (applicationData as any).employeeDetails || {};
  const sapInfo =
    empDetails?.sap_department_designation_wings ||
    profileDetails?.sap_department_designation_wings ||
    storedDetails?.sap_department_designation_wings ||
    {};

  const dateOfJoining =
    empDetails.date_of_joining ||
    profileDetails.date_of_joining ||
    storedDetails.date_of_joining ||
    empDetails.doj ||
    profileDetails.doj ||
    storedDetails.doj ||
    (applicationData as any).dateOfJoining;

  const employmentStatus =
    empDetails.employmentType ||
    profileDetails.employmentType ||
    storedDetails.employmentType ||
    empDetails.employment_status ||
    profileDetails.employment_status ||
    storedDetails.employment_status ||
    empDetails.employeeStatus ||
    profileDetails.employeeStatus ||
    storedDetails.employeeStatus ||
    (applicationData as any).employmentType;

  const designation =
    sapInfo.designation_name ||
    empDetails.designation ||
    profileDetails.designation ||
    storedDetails.designation ||
    empDetails.designation_details?.designationName ||
    profileDetails.designation_details?.designationName ||
    storedDetails.designation_details?.designationName ||
    (applicationData as any).designationName ||
    (applicationData as any).designation;

  const birthdate =
    (applicationData as any).birthdate ||
    (applicationData as any).dateOfBirth ||
    empDetails.birthdate ||
    profileDetails.birthdate ||
    storedDetails.birthdate ||
    empDetails.dob ||
    profileDetails.dob ||
    storedDetails.dob;

  const department =
    empDetails.departmentName ||
    empDetails.department ||
    profileDetails.departmentName ||
    profileDetails.department ||
    storedDetails.departmentName ||
    storedDetails.department ||
    (applicationData as any).departmentName;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.noc.passport}
                className="text-primary text-decoration-none me-3"
              >
                <i className="ti ti-arrow-left fs-24"></i>
              </Link>
              <h3 className="page-title mb-0">
                NOC for Passport / Visa - {applicationData.referenceNumber}
              </h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.passport}>Passport / Visa</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Application Details
                </li>
              </ol>
            </nav>
          </div>


        </div>

        {/* Request Status Banner */}
        <div className={`alert mb-4 ${getStatusBadgeClass(applicationData.requestStatus, applicationData.is_draft).replace('badge', '')} p-3 d-flex align-items-center rounded border`}>
          <i className="ti ti-info-circle me-2 fs-20"></i>
          <div>
            <div className="fs-12 mb-1" style={{ opacity: 0.8 }}>Request Status</div>
            <div className="fw-bold fs-16 text-uppercase">{getStatusText(applicationData.requestStatus, applicationData.is_draft)}</div>
          </div>
        </div>

        {/* 1. Application Details */}
        <div className="card mt-4 border shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center bg-white border-bottom pb-3 pt-3">
            <h4 className="mb-0 text-dark fw-bold fs-16 d-flex align-items-center">
              <i className="ti ti-briefcase me-2 text-danger fs-20"></i>
              Application Details
            </h4>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Ref. No</small>
                  <div className="fw-bold text-dark fs-15">{applicationData.referenceNumber || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Employee ID</small>
                  <div className="fw-bold text-primary fs-15">{formatEmpIdForDisplay(applicationData.empID) || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Currently With</small>
                  <div className="fw-bold text-dark fs-15">{(applicationData as any).approverUserID || (applicationData as any).approverId || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Approver Name</small>
                  <div className="fw-bold text-dark fs-15 text-uppercase">{(applicationData as any).approverName || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Creation Date</small>
                  <div className="fw-bold text-dark fs-15">{formatDate(applicationData.requestCreationDate || (applicationData as any).created_at)}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">D&AR Status</small>
                  <div className="fw-bold text-dark fs-15">{applicationData.dnARStatus || "--"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Remarks by Controlling</small>
                  <div className="fw-bold text-dark fs-15">{(applicationData as any).Remarks || applicationData.remarks || (applicationData as any).Remarks1 || "--"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Employee Details */}
        <div className="card mt-4 border shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center bg-white border-bottom pb-3 pt-3">
            <h4 className="mb-0 text-dark fw-bold fs-16 d-flex align-items-center">
              <i className="ti ti-user-scan me-2 text-danger fs-20"></i>
              Employee Details
            </h4>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Name</small>
                  <div className="fw-bold text-dark fs-15 text-uppercase">{applicationData.employeeName || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Employee ID</small>
                  <div className="fw-bold text-primary fs-15">{formatEmpIdForDisplay(applicationData.empID) || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Father's Name (Full / No abbreviations)</small>
                  <div className="fw-bold text-dark fs-15">{applicationData.fatherName || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Department</small>
                  <div className="fw-bold text-dark fs-15 text-uppercase">{department || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Contact Number</small>
                  <div className="fw-bold text-dark fs-15">{applicationData.contactNumber || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Email ID</small>
                  <div className="fw-bold text-dark fs-15 text-uppercase">{(applicationData as any).email || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Date Of Birth</small>
                  <div className="fw-bold text-dark fs-15">{formatDate(birthdate)}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Date of Joining</small>
                  <div className="fw-bold text-dark fs-15">{formatDate(dateOfJoining)}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Employment status</small>
                  <div className="fw-bold text-dark fs-15">{employmentStatus || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Designation</small>
                  <div className="fw-bold text-dark fs-15 text-uppercase">{designation || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Place of Posting</small>
                  <div className="fw-bold text-dark fs-15">{applicationData.placeOfPosting || (applicationData as any).placeofPosting || (applicationData as any).employee?.placeOfPosting || (applicationData as any).employee?.placeofPosting || (applicationData as any).employee?.details?.placeOfPosting || (applicationData as any).employeeDetails?.placeOfPosting || "-"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Additional Information */}
        <div className="card mt-4 border shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center bg-white border-bottom pb-3 pt-3">
            <h4 className="mb-0 text-dark fw-bold fs-16 d-flex align-items-center">
              <i className="ti ti-map-search me-2 text-danger fs-20"></i>
              Additional Information
            </h4>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-6">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Present Address</small>
                  <div className="fw-bold text-dark fs-15">{applicationData.presentAddress || "-"}</div>
                </div>
              </div>
              <div className="col-md-6 col-lg-6">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Permanent Address</small>
                  <div className="fw-bold text-dark fs-15">{applicationData.permanentAddress || "-"}</div>
                </div>
              </div>
              <div className="col-md-12">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">NOC Applied For</small>
                  <div className="fw-bold text-dark fs-15">{getNOCAppliedForText(applicationData.nOCAppliedFor)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Declaration Details */}
        <div className="card mt-4 border shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center bg-white border-bottom pb-3 pt-3">
            <h4 className="mb-0 text-dark fw-bold fs-16 d-flex align-items-center">
              <i className="ti ti-file-text me-2 text-danger fs-20"></i>
              Declaration Details
            </h4>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-12">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Are you involved in any court/Police/Vigilance Case for which your presence is required?</small>
                  <div className="fw-bold text-dark fs-15 text-lowercase">{formatYesNo(applicationData.pendingCase)}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Whether any D&AR Pending</small>
                  <div className="fw-bold text-dark fs-15 text-lowercase">{formatYesNo(applicationData.dnARPending)}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Whether any Vigilance Pending</small>
                  <div className="fw-bold text-dark fs-15 text-lowercase">{formatYesNo(applicationData.vigilancePending)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Any Remarks */}
        <div className="card mt-4 border shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center bg-white border-bottom pb-3 pt-3">
            <h4 className="mb-0 text-dark fw-bold fs-16 d-flex align-items-center">
              <i className="ti ti-file-description me-2 text-danger fs-20"></i>
              Any Remarks
            </h4>
          </div>
          <div className="card-body">
            {(() => {
              let rawList: any[] = [
                ...(Array.isArray((applicationData as any)?.remarks_data) && (applicationData as any).remarks_data.length > 0
                  ? (applicationData as any).remarks_data
                  : Array.isArray((applicationData as any)?.history) && (applicationData as any).history.length > 0
                  ? (applicationData as any).history
                  : Array.isArray((applicationData as any)?.work_history) && (applicationData as any).work_history.length > 0
                  ? (applicationData as any).work_history
                  : Array.isArray((applicationData as any)?.remarksHistory) && (applicationData as any).remarksHistory.length > 0
                  ? (applicationData as any).remarksHistory
                  : [])
              ].map((item: any) => ({ ...item }));

              const appData: any = applicationData;
              const applicantId = String(
                appData?.empId ||
                appData?.empID ||
                appData?.employee?.empId ||
                ""
              ).replace(/^0+/, "");

              const hasEmployeeEntry = rawList.some((item: any) => {
                const itemUserId = String(
                  item?.approverUserID || item?.userId || item?.empID || ""
                ).replace(/^0+/, "");
                return (
                  item?.usertype === "E" ||
                  item?.usertype === "Employee" ||
                  item?.approverName === "User" ||
                  (applicantId && itemUserId && itemUserId === applicantId)
                );
              });

              const employeeRemarks = (() => {
                const candidates = [
                  appData?.anyRemarks,
                  appData?.employeeRemarks,
                  appData?.remarks !== "X" ? appData?.remarks : null,
                  appData?.Remarks !== "X" ? appData?.Remarks : null,
                ];
                for (const cand of candidates) {
                  if (typeof cand === "string" && cand.trim() && cand.trim() !== "X" && cand.trim() !== "-" && cand.trim() !== "null") {
                    return cand.trim();
                  }
                }
                return "";
              })();

              const employeeTimestamp =
                appData?.requestCreationDate ||
                appData?.created_at ||
                appData?.createdAt ||
                appData?.appliedDate ||
                null;

              const employeeName = (() => {
                if (appData?.employee) {
                  const fname = appData.employee.firstname || appData.employee.firstName || "";
                  const lname = appData.employee.lsastname || appData.employee.lastname || appData.employee.lastName || "";
                  const full = `${fname} ${lname}`.trim();
                  if (full) return full;
                }
                return appData?.employeeName || appData?.empName || "Employee";
              })();

              const employeeUserId = appData?.empId || appData?.empID || appData?.employee?.empId || "";

              if (hasEmployeeEntry) {
                rawList = rawList.map((item: any) => {
                  const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
                  const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
                  if (isEmp && !item?.remarks && employeeRemarks) {
                    return { ...item, remarks: employeeRemarks };
                  }
                  return { ...item };
                });
              } else if (appData && (employeeUserId || employeeName !== "Employee" || appData?.id || appData?.referenceNumber)) {
                rawList.push({
                  usertype: "Employee",
                  approverUserID: employeeUserId,
                  userId: employeeUserId,
                  empID: employeeUserId,
                  approverName: employeeName,
                  name: employeeName,
                  remarks: employeeRemarks,
                  createdAt: employeeTimestamp,
                  isEmployee: true,
                });
              }

              if (rawList.length === 0) {
                return <div className="text-muted p-3 bg-light rounded border text-center">No remarks available.</div>;
              }

              return (
                <div className="d-flex flex-column gap-3">
                  {rawList.map((item: any, index: number) => {
                    const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
                    const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
                    return (
                      <div key={index} className={`p-3 bg-white rounded border border-start-4 ${isEmp ? "border-start-info" : "border-start-primary"} shadow-xs`}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <div className={`avatar avatar-sm ${isEmp ? "bg-info-transparent text-info" : "bg-primary-transparent text-primary"} rounded-circle fw-bold`}>
                              <i className={`${isEmp ? "ti ti-user" : "ti ti-user-check"} fs-16`}></i>
                            </div>
                            <div>
                              <div className="d-flex align-items-center gap-2">
                                <h6 className="mb-0 text-dark fw-bold text-uppercase">
                                  {item.approverName || item.name || item.history_approver?.name || (isEmp ? "Employee" : "Approver")}
                                </h6>
                                <span className={`badge ${isEmp ? "bg-info-subtle text-info" : "bg-warning-subtle text-warning"} border text-xs`}>
                                  {isEmp ? "Employee" : "Approver"}
                                </span>
                              </div>
                              <small className="text-muted">
                                ID: {formatEmpIdForDisplay(item.approverUserID || item.userId || item.history_approver?.empId || (isEmp ? employeeUserId : "") || "")}
                              </small>
                            </div>
                          </div>
                          {(item.createdAt || item.created_at || item.timestamp) && (
                            <span className="badge bg-light text-muted border">
                              <i className="ti ti-calendar me-1"></i>
                              {formatDate(item.createdAt || item.created_at || item.timestamp)}
                              {` ${new Date(item.createdAt || item.created_at || item.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }).toUpperCase()}`}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 pt-2 border-top">
                          <p className="mb-0 text-dark fw-medium fs-14">
                            {item.remarks ? (
                              <span>
                                <i className="ti ti-quote text-muted me-1"></i>
                                {item.remarks}
                              </span>
                            ) : (
                              <span className="text-muted fst-italic">No remarks</span>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>

        {/* 6. Declaration */}
        <div className="card mt-4 border shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center bg-white border-bottom pb-3 pt-3">
            <h4 className="mb-0 text-dark fw-bold fs-16 d-flex align-items-center">
              <i className="ti ti-shield-check me-2 text-danger fs-20"></i>
              Declaration
            </h4>
          </div>
          <div className="card-body">
            <div className="d-flex align-items-start p-3 bg-light rounded border">
              <div className="me-3 mt-1">
                <input type="checkbox" className="form-check-input bg-danger border-danger" checked readOnly style={{ width: "20px", height: "20px" }} />
              </div>
              <div className="text-dark fw-medium fs-15">
                I certify that I am not involved in any court/police/vigilance case and there is no restriction placed by any Authority on foreign travel.
              </div>
            </div>
          </div>
        </div>

        {/* 7. Note */}
        <div className="card mt-4 border-info shadow-sm">
          <div className="card-header bg-info-subtle border-bottom border-info pb-3 pt-3">
            <h5 className="card-title mb-0 text-info d-flex align-items-center">
              <i className="ti ti-info-circle me-2 fs-20"></i>
              Note
            </h5>
          </div>
          <div className="card-body">
            <p className="mb-0 text-dark fw-medium">
              Employee should submit a good quality passport size photograph in HR department after final approval in ESS.
            </p>
          </div>
        </div>

        {/* Chat Component */}
        {applicationData.id && (
          <NOCChatComponent
            nocRequestId={applicationData.id}
            nocType="NOC_PASSPORT"
            employeeName={applicationData.employeeName}
            employeeId={applicationData.empID}
            employeeImage={(applicationData as any).employee?.details?.image || (applicationData as any).employee?.image}
            approverId={
              (applicationData as any).approverUserID ||
              (applicationData as any).approverId ||
              (applicationData as any).reportingManagerID
            }
          />
        )}
      </div>
    </div>
  );
};

export default NOCPassportDetails;
